import {
  Address,
  DateTimeISO,
  Money,
  PaginatedResponse,
  Status,
  StatusQueryParam,
} from './common';

export type CustomersListQueryParams = {
  page: number;
  status?: Status;
  records_per_page?: number;
};

export type PublicCustomer = {
  uuid: string;
  userId: string;
  companyId: string;
  companyName: string;
  firstName: string;
  lastName: string;
  fullName: string;
  cellPhoneNumber: string;
  email: string;
  extId: string;
  status: Status;
  advisors: {
    uuid: string;
  }[];
  adminUrl: string;
};

export type CustomerListItem = {
  uuid: string;
  status: Status;
  firstName: string;
  lastName: string;
  company: {
    uuid: string;
    name: string;
    rateSavingInterest: string;
  };
  email: string;
  avatar: string;
  aggregateInfo: {
    bankApplications: number;
    totalBankApplications: number;
    totalDraftBankApplications: number;
    primaryOwnedBankApplications: number;
    totalPrimaryOwnedBankApplications: number;
    totalActiveBankAccounts: number;
    totalAvailableBalance: Money;
  };
};

export type CustomerListResponse = {
  consumers: PaginatedResponse<CustomerListItem>;
  aggregateInfo: {
    totalBankAccounts: number;
    totalAvailableBalance: Money;
  };
};

export type CustomerDetails = CustomerListItem & {
  kycStatus: string;
  middleName: string;
  ssn: string;
  address: Address;
  mailingAddress: Address;
  isMailingAddressSameCurrentAddr: boolean;
  dateOfBirth: string;
  createdAt?: DateTimeISO;
  deactivatedAt?: DateTimeISO;
  lastLogin: DateTimeISO;
  phoneNumber: string;
  isSelfCreated: boolean;
  hasAcceptedPrivacy: boolean;
  profileStatus: 'incomplete' | 'unconfirmed' | 'confirmed';
  minimumInitDeposit?: Money;
  maximumInitDeposit?: Money;
  advisors: {
    uuid: string;
    firstName: string;
    lastName: string;
    fullName: string;
    value: string;
    label: string;
  }[];
};

export type DocumentListItem = {
  uuid: string;
  filename: string;
  url: string;
  consumerId: string;
  side: 'front' | 'back' | null;
  consumerDocumentType:
    | 'Identity Document'
    | 'Proof of Address Document'
    | 'Other';
};

export type DocumentsListResponse = {
  consumerDocuments: PaginatedResponse<DocumentListItem>;
};
export type ChangeDepositRulesFormFields = {
  uuid: string;
  min: string;
  max: string;
};

export type CustomerFormFields = {
  uuid: string;
  firstName: string;
  middleName: string;
  lastName: string;
  phone: string;
  confirmPhone: string;
  dob: string;
  email: string;
  confirmEmail: string;
  ssn: string;
  confirmSsn: string;
  company: string;
  advisors: string[];
  currentAddress: Address;
  mailingAddress: Address;
  sameMailingAddress: boolean;
  min: string;
  max: string;
};

export type CustomerForSelectionQueryParams = {
  teammate_ids?: string[];
  company_id?: string;
  status?: StatusQueryParam;
};

export type CustomerForSelectItem = {
  uuid: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  lastLogin: DateTimeISO;
};

export const queryKeys = {
  list: ['customers'] as const,
  details: ['customersDetails'] as const,
  selectionList: ['customerSelectionList'] as const,
  documents: ['customerDocuments'] as const,
  preflight: ['customerPreflight'] as const,
};

export enum CustomerDocumentType {
  IDENTITY_DOCUMENT = 'Identity Document',
  PROOF_OF_ADDRESS_DOCUMENT = 'Proof of Address Document',
  OTHER = 'Other',
}

export enum CustomerIdentityDocumentType {
  US_DRIVERS_LICENSE = 'US Drivers License',
  DHS_PERMANENT_RESIDENT_CARD = 'DHS Permanent Resident Card',
  PASSPORT = 'Passport',
  US_VISA = 'US Visa',
  EMPLOYMENT_AUTHORIZATION_DOCUMENT = 'Employment Authorization Document',
  PASSPORT_WITHOUT_US_VISA = 'Passport without US Visa',
  FOREIGN_NATIONAL_IDENTITY_DOCUMENT = 'Foreign National Identity Document',
}

export enum CustomerProofOfAddressDocumentType {
  UTILITY_BILL = 'Utility Bill',
  BANK_OR_CREDIT_CARD_STATEMENT = 'Bank or Credit Card Statement',
  LEASE_AGREEMENT_OR_MORTGAGE_DOCUMENT = 'Lease Agreement or Mortgage Document',
  GOVERNMENT_ISSUED_DOCUMENT_WITH_ADDRESS = 'Government Issued Document with Address',
  PAY_STUB = 'Pay Stub',
  EMPLOYMENT_LETTER = 'Employment Letter',
}

export type AddCustomerDocumentFields = {
  url: string;
  documentDisplayName: '';
  customerId: string;
  side: 'front' | 'back' | '';
  documentType: CustomerDocumentType | '';
  documentSubtype: CustomerIdentityDocumentType | '';
};
